import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Rocket, Loader2, QrCode, Layers,
  Globe, MessageCircle, Palette, Inbox,
} from "lucide-react";
import { api, siteHtmlUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";

const SECTION_LABELS = {
  hero: "Hero Banner", about: "About Us", services: "Services", pricing: "Pricing",
  gallery: "Photo Gallery", testimonials: "Reviews", faq: "FAQ", map: "Location Map", contact: "Contact",
};

export default function SiteDetail() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [site, setSite] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}`);
      setSite(data.site);
    } catch { navigate("/dashboard"); }
  }, [siteId, navigate]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (user?.plan?.status !== "active") {
      navigate("/subscribe", { state: { returnTo: `/sites/${siteId}` } });
      return;
    }
    setPublishing(true);
    try {
      await api.post(`/sites/${siteId}/publish`);
      await refreshUser();
      await load();
      toast.success("Website is live!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not publish");
    } finally { setPublishing(false); }
  };

  if (!site) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C9A24B] wt-spin" /></div>;

  const liveUrl = siteHtmlUrl(siteId);
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&bgcolor=161616&color=E5C378&data=${encodeURIComponent(liveUrl)}`;
  const content = site.generated_content || {};

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">{site.business_name}</h1>
            <p className="text-zinc-500 text-sm mt-1">{site.subdomain}.webtu.in · <span className={site.status === "live" ? "text-[#22C55E]" : "text-[#F59E0B]"}>{site.status}</span></p>
          </div>
          <div className="flex gap-2">
            <a href={liveUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm hover:border-[#C9A24B]/50">
              <ExternalLink className="w-4 h-4" /> View Live
            </a>
            <a data-testid="sitedetail-share-wa"
              href={`https://wa.me/?text=${encodeURIComponent(`Check out ${site.business_name}'s website: ${liveUrl}`)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-sm hover:bg-[#25D366]/20">
              <MessageCircle className="w-4 h-4" /> Share
            </a>
            <a data-testid="sitedetail-view-leads"
              href="#" onClick={(e) => { e.preventDefault(); navigate(`/leads?site=${siteId}`); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#C9A24B]/10 border border-[#C9A24B]/30 text-[#E5C378] text-sm hover:bg-[#C9A24B]/20">
              <Inbox className="w-4 h-4" /> Leads
            </a>
            <button data-testid="sitedetail-publish-btn" onClick={publish} disabled={publishing}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] active:scale-[0.98] disabled:opacity-60">
              {publishing ? <Loader2 className="w-4 h-4 wt-spin" /> : <Rocket className="w-4 h-4" />}
              {site.status === "live" ? "Re-publish" : "Publish"}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {/* Sections */}
          <div className="md:col-span-2 space-y-5">
            <Card icon={Layers} title="Website Sections">
              <div className="grid grid-cols-2 gap-2">
                {(content.sections || []).map((s) => (
                  <div key={s} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#0A0A0A] border border-[#27272A] text-sm">
                    <span className="w-2 h-2 rounded-full bg-[#C9A24B]" />
                    {SECTION_LABELS[s] || s}
                  </div>
                ))}
              </div>
            </Card>

            <div className="grid sm:grid-cols-2 gap-5">
              <Card icon={Palette} title="Theme">
                <p className="text-sm text-zinc-300 capitalize">{(content.theme_id || "").replace("_", " ")}</p>
              </Card>
              <Card icon={MessageCircle} title="WhatsApp Lead Number">
                <p className="text-sm text-zinc-300">+91 {content.business?.whatsapp || content.business?.phone || "—"}</p>
              </Card>
            </div>

            <Card icon={Globe} title="Custom Domain">
              {site.custom_domain ? (
                <div>
                  <div className="text-lg font-bold text-[#E5C378]">{site.custom_domain}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {site.domain?.source === "bought" ? "Purchased through Webtu" : "Linked from your registrar"} · <span className={site.domain?.status === "active" ? "text-[#22C55E]" : "text-[#F59E0B]"}>{(site.domain?.status || "").replaceAll("_", " ")}</span>
                  </div>
                  <button data-testid="sitedetail-manage-domain" onClick={() => navigate(`/sites/${siteId}/domain`)}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm hover:border-[#C9A24B]/50">
                    <Globe className="w-4 h-4" /> Manage domain
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-zinc-400">Your site is on <b className="text-zinc-200">{site.subdomain}.webtu.in</b> (free).
                    Add your own <span className="text-[#E5C378]">.in / .com</span> for a professional look.</p>
                  <button data-testid="sitedetail-buy-domain" onClick={() => navigate(`/sites/${siteId}/domain`)}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] text-sm font-bold active:scale-[0.98]">
                    <Globe className="w-4 h-4" /> Buy / Connect Domain
                  </button>
                </div>
              )}
            </Card>
          </div>

          {/* QR standee */}
          <Card icon={QrCode} title="Shop Counter QR">
            <div className="text-center">
              <img data-testid="sitedetail-qr" src={qr} alt="QR code"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                className="w-44 h-44 mx-auto rounded-xl border border-[#27272A] bg-[#0A0A0A] object-contain" />
              <div style={{ display: "none" }} className="w-44 h-44 mx-auto rounded-xl border border-[#27272A] bg-[#0A0A0A] items-center justify-center text-xs text-zinc-500 text-center px-4">
                QR preview unavailable — open live URL directly
              </div>
              <p className="text-xs text-zinc-500 mt-3">Print & place on your counter. Customers scan to open your site.</p>
              <a href={qr} target="_blank" rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm hover:border-[#C9A24B]/50">
                <ExternalLink className="w-4 h-4" /> Download QR
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-[#161616] border border-[#27272A] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#C9A24B]" />
        <h3 className="font-display font-bold">{title}</h3>
      </div>
      {children}
    </div>
  );
}
